import { useMemo } from "react";
import { Edit, Eye, Power, PowerOff } from "lucide-react";
import { format } from "date-fns";

import { SquadDTO } from "@/types/squad.type";
import { PermissaoEnum } from "@workflow/types";

import { Column, DataTable } from "../table/DataTable";
import { BooleanBadge } from "../table/BooleanBadge";
import { ActionMenu, ActionMenuItem } from "../shared/ActionMenu";
import { PersonBadge } from "../shared/PersonBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import { SquadEditFormDialog } from "./components/SquadEditFormDialog";
import { SquadViewMembersDialog } from "./components/SquadViewMembersDialog";
import { useSquadTable } from "./hooks/use-squad-table"; // Nosso novo hook

interface SquadListTableProps {
  storageKey: string;
}

export function SquadListTable({ storageKey }: SquadListTableProps) {
  const { tableProps, isError, isTogglingStatus, handleConfirmStatusToggle, modals } =
    useSquadTable();

  const squadColumns = useMemo<Column<SquadDTO>[]>(
    () => [
      {
        type: "headless",
        key: "actions",
        title: "Ações",
        visible: true,
        width: "w-[40px]",
        render: (squad) => (
          <ActionMenu
            items={[
              {
                label: "Visualizar Membros",
                icon: <Eye className="h-4 w-4" />,
                onClick: () => modals.view.openModal(squad),
                permissions: [PermissaoEnum.ADMIN],
              },
              {
                label: "Editar Squad",
                icon: <Edit className="h-4 w-4" />,
                onClick: () => modals.edit.openModal(squad),
                permissions: [PermissaoEnum.ADMIN],
              },
              { label: "Separador", separator: true },
              {
                label: squad.ativo ? "Inativar" : "Reativar",
                icon: squad.ativo ? (
                  <PowerOff className="h-4 w-4" />
                ) : (
                  <Power className="h-4 w-4" />
                ),
                danger: squad.ativo,
                onClick: () => modals.status.openModal(squad),
                permissions: [PermissaoEnum.ADMIN],
              },
            ]}
          />
        ),
      },
      {
        key: "squad.id",
        title: "ID",
        sortable: true,
        width: "w-[80px]",
        render: (squad) => <span className="font-medium text-primary">{squad.id}</span>,
        raw: (squad) => squad.id.toString(),
      },
      {
        key: "squad.nome",
        title: "Squad",
        sortable: true,
        width: "min-w-[250px]",
        render: (squad) => squad.nome,
        raw: (squad) => squad.nome,
      },
      {
        key: "squad.ativo",
        title: "Status",
        sortable: true,
        width: "min-w-[100px]",
        render: (squad) => <BooleanBadge value={squad.ativo} />,
        raw: (squad) => squad.ativo.toString(),
      },
      {
        key: "criadoPor.nome",
        title: "Criado por",
        sortable: true,
        render: (squad) =>
          squad?.criadoPor ? <PersonBadge pessoa={squad.criadoPor} isCompact={true} /> : "-",
        raw: (squad) => squad.criadoPor?.nome || "",
      },
      {
        key: "squad.criadoEm",
        title: "Criado em",
        sortable: true,
        width: "min-w-[120px]",
        render: (squad) => (
          <span className="text-sm text-muted-foreground">
            {squad.criadoEm ? format(new Date(squad.criadoEm), "dd/MM/yyyy HH:mm") : "-"}
          </span>
        ),
        raw: (squad) => squad.criadoEm?.toString() || "",
      },
    ],
    [modals],
  );

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey={storageKey}
        columns={squadColumns}
        emptyMessage={
          isError
            ? "Não foi possível carregar as squads. Tente recarregar."
            : "Nenhuma squad encontrada."
        }
        onRowDoubleClick={(squad) => modals.edit.openModal(squad)}
        defaultSortColumn="squad.id"
        defaultSortOrder="DESC"
        searchPlaceholder="Pesquisar squads por nome ou ID..."
        className="border-none"
        rowClassName={(squad) => (!squad.ativo ? "opacity-50" : "")}
      />

      {/* Os modais ficam super limpos, consumindo o estado direto do hook */}
      <SquadEditFormDialog
        open={modals.edit.open}
        onOpenChange={modals.edit.setOpen}
        squadToEdit={modals.edit.data}
      />

      <SquadViewMembersDialog
        open={modals.view.open}
        onOpenChange={modals.view.setOpen}
        squadToView={modals.view.data}
      />

      <AlertDialog open={modals.status.open} onOpenChange={modals.status.setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmar {modals.status.data?.ativo ? "Inativação" : "Reativação"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Você tem certeza que deseja {modals.status.data?.ativo ? "inativar" : "reativar"} o
              squad <span className="font-bold text-foreground">{modals.status.data?.nome}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTogglingStatus}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmStatusToggle();
              }}
              disabled={isTogglingStatus}
              className={
                modals.status.data?.ativo
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }
            >
              {isTogglingStatus ? "Aguarde..." : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
